'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface RoleOption {
  user_id: string;
  user_type: string;
  is_primary: boolean;
  rag_count: number;
  tenant_name: string | null;
  customer_name: string | null;
}

interface RoleSelectionModalProps {
  isOpen: boolean;
  roles: RoleOption[];
  userEmail: string;
  onSelectRole: (userType: string) => void;
}

export default function RoleSelectionModal({
  isOpen,
  roles,
  userEmail,
  onSelectRole,
}: RoleSelectionModalProps) {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedRole) return;
    setIsSubmitting(true);
    onSelectRole(selectedRole);
  };

  const getUserTypeLabel = (userType: string): string => {
    switch (userType) {
      case 'ROOT': return 'Platform Admin';
      case 'TENANT': return 'Tenant Admin';
      case 'CUSTOMER': return 'Customer';
      case 'AGENT': return 'Agent';
      default: return userType;
    }
  };

  const getUserTypeIcon = (userType: string): string => {
    switch (userType) {
      case 'ROOT': return 'ShieldCheckIcon';
      case 'TENANT': return 'BuildingOfficeIcon';
      case 'CUSTOMER': return 'UserIcon';
      case 'AGENT': return 'CogIcon';
      default: return 'UserIcon';
    }
  };

  const getUserTypeDescription = (role: RoleOption): string => {
    switch (role.user_type) {
      case 'ROOT':
        return 'Manage the entire platform, all tenants and customers';
      case 'TENANT':
        return `Manage ${role.tenant_name || 'your agency'}: customers, agents, and settings`;
      case 'CUSTOMER':
        return `Manage ${role.customer_name || 'your organization'}: agents and settings`;
      case 'AGENT':
        return role.rag_count === 1
          ? `Work with 1 assigned agent`
          : `Work with ${role.rag_count} assigned agents`;
      default:
        return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="UserCircleIcon" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Welcome Back!</h2>
              <p className="text-sm text-text-secondary">{userEmail}</p>
            </div>
          </div>
          <p className="text-sm text-text-secondary mt-3">
            You have access to multiple roles. Please select how you'd like to continue:
          </p>
        </div>

        {/* Role Options */}
        <div className="p-6 space-y-3 max-h-[50vh] overflow-y-auto">
          {roles.map((role) => (
            <button
              key={role.user_id}
              onClick={() => setSelectedRole(role.user_type)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedRole === role.user_type
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-primary/50 hover:bg-surface-secondary'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selectedRole === role.user_type
                      ? 'bg-primary/20'
                      : 'bg-muted'
                  }`}
                >
                  <Icon
                    name={getUserTypeIcon(role.user_type)}
                    size={24}
                    className={selectedRole === role.user_type ? 'text-primary' : 'text-text-secondary'}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-text-primary">
                      {getUserTypeLabel(role.user_type)}
                    </h3>
                    {role.is_primary && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                        Primary
                      </span>
                    )}
                    {role.rag_count > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {role.rag_count} {role.rag_count === 1 ? 'agent' : 'agents'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-secondary">
                    {getUserTypeDescription(role)}
                  </p>
                </div>

                {/* Checkmark */}
                {selectedRole === role.user_type && (
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="CheckIcon" size={16} className="text-white" />
                    </div>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center justify-between">
            <p className="text-xs text-text-secondary">
              You can switch roles anytime using the role switcher in the header.
            </p>
            <button
              onClick={handleSubmit}
              disabled={!selectedRole || isSubmitting}
              className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <Icon name="ArrowRightIcon" size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
